import { TimeRecord } from '../models/shared/TimeRecord';
import { formatDurationFromMs, formatDate } from '../utils/date';

export type GroupBy = 'deal' | 'person' | 'activity';

export type ColumnGrouping = 'day' | 'week';

export const UNASSIGNED_ACTIVITY_KEY = 'unassigned';

export type TimePeriodFilter =
    'last7days' | 'last30days' | 'thisMonth' | 'lastMonth' | 'custom';

export function getDateRangeForFilter(
    filter: TimePeriodFilter
): { startDate: Date; endDate: Date } {
    const now = new Date();
    const today = new Date(now);
    today.setHours(23, 59, 59, 999);

    switch (filter) {
        case 'last7days': {
            const start = new Date(now);
            start.setDate(start.getDate() - 6);
            start.setHours(0, 0, 0, 0);
            return { startDate: start, endDate: today };
        }
        case 'last30days': {
            const start = new Date(now);
            start.setDate(start.getDate() - 29);
            start.setHours(0, 0, 0, 0);
            return { startDate: start, endDate: today };
        }
        case 'thisMonth': {
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            start.setHours(0, 0, 0, 0);
            return { startDate: start, endDate: today };
        }
        case 'lastMonth': {
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            start.setHours(0, 0, 0, 0);

            const end = new Date(now.getFullYear(), now.getMonth(), 0);
            end.setHours(23, 59, 59, 999);

            return { startDate: start, endDate: end };
        }
        default:
            return { startDate: today, endDate: today };
    }
};

export function getRecordDurationForDay(
    record: TimeRecord,
    targetDate: Date
): number {
    const endTime = record.endAt || new Date();

    const dayStart = new Date(targetDate);
    dayStart.setHours(0, 0, 0, 0);

    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);

    const overlapStart = new Date(
        Math.max(record.startAt.getTime(), dayStart.getTime())
    );

    const overlapEnd = new Date(
        Math.min(endTime.getTime(), dayEnd.getTime())
    );

    return overlapStart >= overlapEnd
        ? 0
        : overlapEnd.getTime() - overlapStart.getTime();
};

export function getGroupKeysForRecord(
    record: TimeRecord,
    groupBy: GroupBy
): string[] {
    if (groupBy === 'deal') {
        return [record.dealId];
    }

    if (groupBy === 'person') {
        return [record.email];
    }

    if (!record.activitySplits || record.activitySplits.length === 0) {
        return [UNASSIGNED_ACTIVITY_KEY];
    }

    const activityIds: string[] = [];

    for (const split of record.activitySplits) {
        activityIds.push(split.activityId);
    }

    return activityIds;
}

export function getGroupContributionForDay(
    record: TimeRecord,
    groupKey: string,
    date: Date,
    groupBy: GroupBy
): number {
    const dayDuration = getRecordDurationForDay(record, date);

    if (dayDuration <= 0) {
        return 0;
    }

    if (groupBy === 'deal') {
        if (record.dealId === groupKey) {
            return dayDuration;
        }

        return 0;
    }

    if (groupBy === 'person') {
        if (record.email === groupKey) {
            return dayDuration;
        }

        return 0;
    }

    // Group by activity
    if (!record.activitySplits || record.activitySplits.length === 0) {
        if (groupKey === UNASSIGNED_ACTIVITY_KEY) {
            return dayDuration;
        }

        return 0;
    }

    let split;

    for (const activitySplit of record.activitySplits) {
        if (activitySplit.activityId === groupKey) {
            split = activitySplit;
            break;
        }
    }
    
    if (!split) {
        return 0;
    }

    const endTime = record.endAt || new Date();
    const recordTotalMs = endTime.getTime() - record.startAt.getTime();

    if (recordTotalMs <= 0) {
        return 0;
    }

    const contribution = dayDuration * (split.durationMs / recordTotalMs);

    return contribution;
}

export interface TimesheetColumn {
    label: string;
    exportLabel: string;
    dates: Date[];
}

export interface TimesheetCell {
    duration: number;
    // Records that contributed to this cell, so the UI can show them without recomputing.
    records: TimeRecord[];
}

export interface TimesheetRow {
    key: string;
    cells: TimesheetCell[];
    total: number;
}

export interface TimesheetTable {
    columns: TimesheetColumn[];
    rows: TimesheetRow[];
    totals: { cells: number[]; total: number };
}

function getDatesInRange(startDate: Date, endDate: Date): Date[] {
    const dates: Date[] = [];
    const current = new Date(startDate);
    current.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    while (current <= end) {
        dates.push(new Date(current));
        current.setDate(current.getDate() + 1);
    }
    return dates;
}

function getIsoWeekNumber(monday: Date): number {
    const thursday = new Date(monday);
    thursday.setDate(thursday.getDate() + 3);

    return Math.floor(
        (Date.UTC(thursday.getFullYear(), thursday.getMonth(), thursday.getDate())
            - Date.UTC(thursday.getFullYear(), 0, 1)) / 86400000 / 7
    ) + 1;
}

export function buildTimesheetColumns(
    startDate: Date,
    endDate: Date,
    grouping: ColumnGrouping,
    weekShortLabel: string
): TimesheetColumn[] {
    const dates = getDatesInRange(startDate, endDate);

    if (grouping === 'day') {
        return dates.map(date => {
            const weekDay = date.toLocaleDateString(undefined, { weekday: 'short' }).replace(/\.$/, '');
            return {
                label: `${weekDay} ${date.getDate()}`,
                exportLabel: formatDate(date, { isoDate: true, noTime: true }),
                dates: [date],
            };
        });
    }

    const columns: TimesheetColumn[] = [];
    let currentWeekKey = '';

    for (const date of dates) {
        const monday = new Date(date);
        monday.setDate(monday.getDate() - (monday.getDay() + 6) % 7);
        const weekKey = formatDate(monday, { isoDate: true, noTime: true });

        if (weekKey !== currentWeekKey) {
            const label = `${weekShortLabel}${getIsoWeekNumber(monday)}`;
            columns.push({ label, exportLabel: label, dates: [] });
            currentWeekKey = weekKey;
        }

        columns[columns.length - 1].dates.push(date);
    }

    return columns;
}

export function buildTimesheetTable(
    records: TimeRecord[],
    groupBy: GroupBy,
    columns: TimesheetColumn[],
    // Keys listed here come first, in this order; the rest follow in order of first appearance.
    groupOrder: string[] = []
): TimesheetTable {
    const rowMap = new Map<string, TimesheetRow>();
    const totals = { cells: columns.map(() => 0), total: 0 };

    for (const record of records) {
        for (const key of getGroupKeysForRecord(record, groupBy)) {
            let row = rowMap.get(key);
            if (!row) {
                row = { key, cells: columns.map(() => ({ duration: 0, records: [] })), total: 0 };
                rowMap.set(key, row);
            }

            for (let ix = 0; ix < columns.length; ix++) {
                let duration = 0;
                for (const date of columns[ix].dates) {
                    duration += getGroupContributionForDay(record, key, date, groupBy);
                }

                if (duration > 0) {
                    row.cells[ix].duration += duration;
                    row.cells[ix].records.push(record);
                    row.total += duration;
                    totals.cells[ix] += duration;
                    totals.total += duration;
                }
            }
        }
    }

    const position = (key: string) => {
        const ix = groupOrder.indexOf(key);
        return ix === -1 ? groupOrder.length : ix;
    };
    const rows = Array.from(rowMap.values())
        .filter(row => row.total > 0)
        .sort((a, b) => position(a.key) - position(b.key));

    return { columns, rows, totals };
}

export function escapeCsvField(value: string): string {
    const needsQuotes =
        value.includes('"') ||
        value.includes(';') ||
        value.includes('\n');

    if (needsQuotes) {
        const escapedValue = value.replace(/"/g, '""');
        return '"' + escapedValue + '"';
    }

    return value;
}

type GenerateTimesheetCsvOptions = {
    table: TimesheetTable;
    groupByLabel: string;
    totalHoursLabel: string;
    totalLabel: string;
    getGroupName: (groupKey: string) => Promise<string>;
};

const formatCsvDuration = (duration: number) => duration > 0 ? formatDurationFromMs(duration) : '';

export async function generateTimesheetCsv(
    options: GenerateTimesheetCsvOptions
): Promise<string> {
    const { table, groupByLabel, totalHoursLabel, totalLabel, getGroupName } = options;

    const allRows: string[][] = [
        [groupByLabel, ...table.columns.map(column => column.exportLabel), totalHoursLabel],
    ];

    for (const row of table.rows) {
        allRows.push([
            await getGroupName(row.key),
            ...row.cells.map(cell => formatCsvDuration(cell.duration)),
            formatDurationFromMs(row.total),
        ]);
    }

    allRows.push([
        totalLabel,
        ...table.totals.cells.map(formatCsvDuration),
        formatDurationFromMs(table.totals.total),
    ]);

    return allRows
        .map(row => row.map(field => escapeCsvField(field)).join(';'))
        .join('\n');
}
